import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  ClipboardList, History, Zap, FolderKanban, AlertTriangle, LayoutGrid, Users,
  Settings, Bell, LogOut, Menu, X, Globe, FileBarChart, ShieldAlert, KeyRound, ListChecks, Terminal,
} from "lucide-react";
import GlobalSearch from "./GlobalSearch";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../lib/i18n";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import ChangePasswordDialog from "./ChangePasswordDialog";

const NAV = [
  {
    section: "myWork",
    items: [
      { to: "/my-work", label: "myWorkPage", icon: ListChecks, roles: ["TEAM_MEMBER", "PM", "LEAD", "ADMIN"] },
      { to: "/query", label: "workQuery", icon: Terminal, roles: ["TEAM_MEMBER", "PM", "LEAD", "EXECUTIVE", "ADMIN", "AUDITOR"] },
      { to: "/my-daily", label: "myDaily", icon: ClipboardList, roles: ["TEAM_MEMBER", "PM", "ADMIN"] },
      { to: "/my-history", label: "myHistory", icon: History, roles: ["TEAM_MEMBER", "PM", "ADMIN"] },
    ],
  },
  {
    section: "delivery",
    items: [
      { to: "/action-center", label: "actionCenter", icon: Zap, roles: ["PM", "LEAD", "ADMIN"] },
      { to: "/projects", label: "projects", icon: FolderKanban, roles: ["PM", "LEAD", "EXECUTIVE", "ADMIN", "AUDITOR"] },
      { to: "/warnings", label: "warnings", icon: AlertTriangle, roles: ["PM", "LEAD", "EXECUTIVE", "ADMIN", "AUDITOR"] },
      { to: "/blockers", label: "blockerCenter", icon: ShieldAlert, roles: ["PM", "LEAD", "EXECUTIVE", "ADMIN", "AUDITOR"] },
      { to: "/portfolio", label: "portfolio", icon: LayoutGrid, roles: ["PM", "LEAD", "EXECUTIVE", "ADMIN", "AUDITOR"] },
      { to: "/team-workload", label: "teamWorkload", icon: Users, roles: ["PM", "LEAD", "EXECUTIVE", "ADMIN", "AUDITOR"] },
    ],
  },
  {
    section: "reportsSection",
    items: [
      { to: "/reports", label: "reportCenter", icon: FileBarChart, roles: ["PM", "LEAD", "EXECUTIVE", "ADMIN", "AUDITOR"] },
    ],
  },
  {
    section: "administration",
    items: [{ to: "/admin", label: "admin", icon: Settings, roles: ["ADMIN", "AUDITOR"] }],
  },
];

function NotificationBell() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], unread: 0 });
  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, []);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="notifications-bell" className="relative p-2 rounded-md hover:bg-secondary transition-colors">
          <Bell className="h-5 w-5 text-muted-foreground" />
          {data.unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-0.5 rounded-full bg-[#DC2626] text-white text-[10px] flex items-center justify-center">
              {data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0 max-h-96 overflow-y-auto">
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <span className="text-sm font-semibold">{t("notifications")}</span>
          <button
            data-testid="notifications-mark-all-read"
            className="text-xs text-primary hover:underline"
            onClick={() => api.post("/notifications/read-all").then(load)}
          >
            {t("markAllRead")}
          </button>
        </div>
        {data.items.length === 0 && (
          <div className="p-4 text-sm text-muted-foreground">{t("noNotifications")}</div>
        )}
        {data.items.map((n) => (
          <div
            key={n.id}
            data-testid={`notification-${n.id}`}
            onClick={() => { if (n.entity_type === "work_item" && n.entity_id) { api.post(`/notifications/${n.id}/read`).catch(() => {}); navigate(`/work/${n.entity_id}`); } }}
            className={`px-3 py-2 border-b last:border-0 text-sm ${n.read ? "opacity-60" : "bg-accent/40"} ${n.entity_type === "work_item" ? "cursor-pointer hover:bg-secondary" : ""}`}
          >
            <div className="font-medium">{n.title}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{n.body}</div>
          </div>
        ))}
      </PopoverContent>
    </Popover>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useLang();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);

  const sections = NAV.map((s) => ({
    ...s,
    items: s.items.filter((i) => i.roles.includes(user.role)),
  })).filter((s) => s.items.length > 0);

  const sidebar = (
    <nav className="flex flex-col gap-5 p-4">
      {sections.map((s) => (
        <div key={s.section}>
          <div className="text-[11px] font-semibold tracking-[0.1em] text-slate-400 px-2 mb-1.5">
            {t(s.section)}
          </div>
          {s.items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label}`}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-[#1E3A8A] text-white font-medium"
                    : "text-slate-600 hover:bg-secondary"
                }`
              }
            >
              <item.icon className="h-4 w-4 shrink-0" />
              {t(item.label)}
            </NavLink>
          ))}
        </div>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen flex text-left">
      <aside className="hidden lg:flex w-60 flex-col border-r bg-white shrink-0 sticky top-0 h-screen overflow-y-auto">
        <div className="px-4 py-4 border-b">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-md bg-[#1E3A8A] text-white flex items-center justify-center font-heading font-bold text-sm">
              A
            </div>
            <div>
              <div className="font-heading font-semibold text-base leading-tight">APHA</div>
              <div className="text-[10px] text-muted-foreground leading-tight">{t("appTagline")}</div>
            </div>
          </div>
        </div>
        {sidebar}
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-64 bg-white overflow-y-auto">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <span className="font-heading font-semibold">APHA</span>
              <button onClick={() => setMobileOpen(false)} data-testid="mobile-nav-close">
                <X className="h-5 w-5" />
              </button>
            </div>
            {sidebar}
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b">
          <div className="flex items-center gap-3 px-4 h-14">
            <button className="lg:hidden p-2" onClick={() => setMobileOpen(true)} data-testid="mobile-nav-open">
              <Menu className="h-5 w-5" />
            </button>
            <div className="flex-1 flex items-center">
              <GlobalSearch />
            </div>
            <div className="flex items-center gap-1 border rounded-md p-0.5">
              <Globe className="h-3.5 w-3.5 text-muted-foreground ml-1" />
              {["en", "id"].map((l) => (
                <button
                  key={l}
                  data-testid={`lang-toggle-${l}`}
                  onClick={() => setLang(l)}
                  className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                    lang === l ? "bg-[#1E3A8A] text-white" : "text-slate-500 hover:bg-secondary"
                  }`}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
            <NotificationBell />
            <div className="flex items-center gap-2 pl-2 border-l">
              <div className="hidden sm:block text-right">
                <div className="text-sm font-medium leading-tight" data-testid="header-user-name">
                  {user.display_name}
                </div>
                <div className="text-[10px] text-muted-foreground leading-tight">{user.role}</div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                data-testid="change-password-button"
                title={t("changePassword")}
                onClick={() => setPwOpen(true)}
              >
                <KeyRound className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                data-testid="logout-button"
                onClick={() => {
                  logout();
                  navigate("/login");
                }}
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1400px] w-full mx-auto">{children}</main>
      </div>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </div>
  );
}
